import React, { useContext, useEffect, useState } from "react";
import {
  ContentCopyRounded,
  LaunchRounded,
  TagRounded,
} from "@mui/icons-material";
import { Alert, Box, Button, Chip, Snackbar, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import Prism from "prismjs";
import "prismjs/themes/prism.css";
import "prismjs/components/prism-markup";
import "prismjs/components/prism-javascript";
import Logo from "../../images/gtm-icon.png";
import { AppVarsContext } from "../../context/AppVarsContext";
import GlassPageShell from "../../components/glass/GlassPageShell";
import GlassPanel from "../../components/glass/GlassPanel";

const SnippetPanel = ({
  eyebrow,
  title,
  description,
  code,
  copyLabel,
  copyMessage,
  cmsLink,
  cmsLabel,
  siteLink,
  siteLabel,
  appAppearance,
  onCopy,
}) => {
  const primaryButtonSx = {
    borderRadius: "999px",
    px: 1.5,
    py: 1,
    textTransform: "none",
    fontWeight: 700,
    boxShadow: "none",
    background: `linear-gradient(135deg, ${appAppearance.colors.primary} 0%, ${appAppearance.colors.secondary} 100%)`,
    "&:hover": {
      boxShadow: appAppearance.shadows.raised,
      background: `linear-gradient(135deg, ${appAppearance.colors.primary} 0%, ${appAppearance.colors.secondary} 100%)`,
    },
  };

  const secondaryButtonSx = {
    borderRadius: "999px",
    px: 1.5,
    py: 1,
    textTransform: "none",
    fontWeight: 700,
    boxShadow: "none",
    color: appAppearance.colors.textPrimary,
    bgcolor: alpha("#ffffff", 0.66),
    border: `1px solid ${appAppearance.colors.border}`,
    "&:hover": {
      boxShadow: "none",
      bgcolor: alpha("#ffffff", 0.76),
      borderColor: appAppearance.colors.borderStrong,
    },
  };

  return (
    <GlassPanel sx={{ p: { xs: 2, md: 2.5 }, height: "100%" }}>
      <Stack spacing={2}>
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: appAppearance.colors.textMuted,
              letterSpacing: "0.16em",
              fontWeight: 700,
            }}
          >
            {eyebrow}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: appAppearance.colors.textPrimary,
              fontWeight: 800,
              letterSpacing: "-0.03em",
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 0.75,
              color: appAppearance.colors.textSecondary,
              lineHeight: 1.7,
            }}
          >
            {description}
          </Typography>
        </Box>

        <Box
          sx={{
            p: 2,
            borderRadius: 3,
            border: `1px solid ${appAppearance.colors.codeBorder}`,
            background: appAppearance.colors.codeBackground,
            overflowX: "auto",
          }}
        >
          <code
            className="language-html"
            style={{
              margin: 0,
              display: "block",
              color: appAppearance.colors.codeText,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.7,
              fontSize: "0.92rem",
            }}
          >
            {code || "Loading..."}
          </code>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          useFlexGap
          flexWrap="wrap"
        >
          <Button
            sx={primaryButtonSx}
            onClick={() => onCopy(code, copyMessage)}
            endIcon={<ContentCopyRounded />}
          >
            {copyLabel}
          </Button>

          {cmsLink ? (
            <Button
              sx={secondaryButtonSx}
              target="_blank"
              href={cmsLink}
              endIcon={<LaunchRounded />}
            >
              {cmsLabel}
            </Button>
          ) : null}

          {siteLink ? (
            <Button
              sx={secondaryButtonSx}
              target="_blank"
              href={siteLink}
              endIcon={<LaunchRounded />}
            >
              {siteLabel}
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </GlassPanel>
  );
};

const GtmComponent = ({ clientID }) => {
  const [gtm, setGtm] = useState(null);
  const [script, setScript] = useState({ script: "", img: "" });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const { appVars, appAppearance } = useContext(AppVarsContext);

  useEffect(() => {
    if (!appVars?.api_url || !clientID) {
      return undefined;
    }

    let isActive = true;

    const fetchData = async () => {
      setLoading(true);

      try {
        const res = await fetch(`${appVars.api_url}/tagmanagers/${clientID}`);
        const data = await res.json();

        if (!isActive) {
          return;
        }

        setGtm(data);
        setScript({
          script: appVars.gtmScript.replace("%%gtmid%%", data.tag),
          img: appVars.gtmImage.replace("%%gtmid%%", data.tag),
        });
      } catch (error) {
        if (isActive) {
          setMessage("Unable to load GTM instructions.");
          setOpen(true);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isActive = false;
    };
  }, [appVars, clientID]);

  useEffect(() => {
    Prism.highlightAll();
  }, [script.script, script.img]);

  const handleClose = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setMessage("");
    setOpen(false);
  };

  const copyToClipboard = async (value, successMessage) => {
    if (!value) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.setAttribute("readonly", "readonly");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setMessage(successMessage);
      setOpen(true);
    } catch (error) {
      setMessage("Copy to clipboard failed.");
      setOpen(true);
    }
  };

  const heroAside = (
    <GlassPanel
      sx={{
        height: "100%",
        p: { xs: 2, md: 2.5 },
        background: `linear-gradient(160deg, ${alpha("#ffffff", 0.78)} 0%, ${alpha(appAppearance.colors.secondary, 0.12)} 100%)`,
      }}
    >
      <Stack spacing={2} height="100%">
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(appAppearance.colors.primary, 0.12),
              border: `1px solid ${appAppearance.colors.border}`,
            }}
          >
            <Box component="img" src={Logo} alt="" sx={{ width: 26, height: 26 }} />
          </Box>
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: appAppearance.colors.textMuted,
                letterSpacing: "0.16em",
                fontWeight: 700,
              }}
            >
              Overview
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: appAppearance.colors.textPrimary, fontWeight: 700 }}
            >
              {loading ? "Loading container" : gtm?.name || "Google Tag Manager"}
            </Typography>
          </Box>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          {[
            { label: "Container", value: loading ? "Loading..." : gtm?.tag || "Pending" },
            { label: "CMS", value: loading ? "Loading..." : gtm?.cms_name || "Custom" },
            { label: "Site Guide", value: loading ? "Loading..." : gtm?.site || "General" },
            { label: "Snippets", value: "2 required" },
          ].map((entry) => (
            <Box
              key={entry.label}
              sx={{
                p: 1.25,
                borderRadius: 2.5,
                border: `1px solid ${appAppearance.colors.border}`,
                bgcolor: alpha("#ffffff", 0.56),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: appAppearance.colors.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  fontWeight: 700,
                }}
              >
                {entry.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: appAppearance.colors.textPrimary,
                  fontWeight: 700,
                }}
              >
                {entry.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Chip
            icon={<TagRounded sx={{ color: appAppearance.colors.primary }} />}
            label={loading ? "Container loading" : gtm?.tag || "Container pending"}
            sx={{
              borderRadius: "999px",
              bgcolor: alpha("#ffffff", 0.58),
              border: `1px solid ${appAppearance.colors.border}`,
              "& .MuiChip-label": {
                px: 1.3,
                fontWeight: 700,
                color: appAppearance.colors.textPrimary,
              },
            }}
          />
        </Stack>
      </Stack>
    </GlassPanel>
  );

  return (
    <GlassPageShell
      eyebrow="Implementation Guide"
      title={loading ? "Google Tag Manager" : gtm?.name || "Google Tag Manager"}
      description="Use the two snippets below to install Tag Manager. Place the head script near the opening head tag, then place the noscript iframe directly after the opening body tag."
      badge={loading ? "Loading container" : gtm?.tag || "GTM"}
      heroAside={heroAside}
      maxWidth={1520}
    >
      <GlassPanel
        sx={{
          p: { xs: 2, md: 2.5 },
          background: `linear-gradient(135deg, ${alpha("#ffffff", 0.76)} 0%, ${alpha(appAppearance.colors.accent, 0.08)} 100%)`,
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: appAppearance.colors.textMuted,
                letterSpacing: "0.16em",
                fontWeight: 700,
              }}
            >
              Instructions
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: appAppearance.colors.textPrimary,
                fontWeight: 800,
                letterSpacing: "-0.03em",
              }}
            >
              Installation Steps
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
              gap: 1.25,
            }}
          >
            {[
              {
                step: "01",
                title: "Place the head snippet",
                body: "Add the JavaScript block as close to the opening head tag as possible on every page.",
              },
              {
                step: "02",
                title: "Add the noscript fallback",
                body: "Insert the iframe version immediately after the opening body tag to preserve non-JavaScript coverage.",
              },
              {
                step: "03",
                title: "Use platform guides when needed",
                body: "Open the CMS or site-specific documentation below if this client uses a managed template or platform.",
              },
            ].map((entry) => (
              <Box
                key={entry.step}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  border: `1px solid ${appAppearance.colors.border}`,
                  bgcolor: alpha("#ffffff", 0.54),
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: appAppearance.colors.primary,
                    letterSpacing: "0.14em",
                    fontWeight: 800,
                  }}
                >
                  Step {entry.step}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mt: 0.5,
                    color: appAppearance.colors.textPrimary,
                    fontWeight: 700,
                  }}
                >
                  {entry.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.75,
                    color: appAppearance.colors.textSecondary,
                    lineHeight: 1.7,
                  }}
                >
                  {entry.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Stack>
      </GlassPanel>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        <SnippetPanel
          eyebrow="Head Snippet"
          title="JavaScript Container"
          description="Copy this block and place it inside the head of every page in the site template."
          code={script.script}
          copyLabel="Copy head snippet"
          copyMessage="GTM head snippet copied to clipboard."
          cmsLink={gtm?.cms_link}
          cmsLabel={`${gtm?.cms_name || "CMS"} guide`}
          siteLink={gtm?.site !== "unknown" ? gtm?.link : ""}
          siteLabel={`${gtm?.site || "Site"} guide`}
          appAppearance={appAppearance}
          onCopy={copyToClipboard}
        />

        <SnippetPanel
          eyebrow="Body Fallback"
          title="Noscript Iframe"
          description="Place this fallback immediately after the opening body tag to support non-JavaScript rendering."
          code={script.img}
          copyLabel="Copy noscript block"
          copyMessage="GTM noscript block copied to clipboard."
          cmsLink={gtm?.cms_link}
          cmsLabel={`${gtm?.cms_name || "CMS"} guide`}
          siteLink={gtm?.site !== "unknown" ? gtm?.link : ""}
          siteLabel={`${gtm?.site || "Site"} guide`}
          appAppearance={appAppearance}
          onCopy={copyToClipboard}
        />
      </Box>

      <Snackbar open={open} autoHideDuration={2200} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success" variant="filled" sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
    </GlassPageShell>
  );
};

export default GtmComponent;
